import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/axios';

// Resolves a usable <img> src from either a full URL or a relative storage path.
const resolveLogoSrc = (logo, BACKEND_URL) => {
  if (!logo) return null;
  return logo.startsWith('http') ? logo : `${BACKEND_URL}/storage/${logo}`;
};

function formatSalary(min, max, currency) {
  if (!min && !max) return null;
  const symbol = currency === 'INR' ? '₹' : (currency ? `${currency} ` : '');
  const fmt = (n) => Math.round(n / 1000) + 'K';
  if (min && max) return `${symbol}${fmt(min)} - ${symbol}${fmt(max)}`;
  return `${symbol}${fmt(min || max)}`;
}

function formatExperience(min, max) {
  if (min == null && max == null) return null;
  if (min != null && max != null) return `${min}-${max} yrs`;
  return `${min ?? max}+ yrs`;
}

function AnimatedCount({ value, duration = 700 }) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (value == null) return;
    let raf;
    let start = null;

    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      setDisplay(Math.floor(progress * value));
      if (progress < 1) raf = requestAnimationFrame(step);
      else setDisplay(value);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);

  return <>{display}</>;
}

// Compact single-line breakdown ("Location 25/30 · Career 30/35 · Skills 28/35")
// for AggregatedJobCard's non-expanding layout. Mirrors JobCard's MatchBreakdownBar
// data but rendered as inline stats rather than progress bars, since this card
// has no expanded state to host bars in.
function MatchBreakdownRow({ breakdown }) {
  if (!breakdown) return null;
  const { location_score, career_score, skills_score } = breakdown;
  if (location_score == null && career_score == null && skills_score == null) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[10px] font-medium text-gray-400">
      {location_score != null && (
        <span>
          <span className="text-purple-500 font-semibold">Location</span> {location_score}/30
        </span>
      )}
      {career_score != null && (
        <span>
          <span className="text-blue-500 font-semibold">Career</span> {career_score}/35
        </span>
      )}
      {skills_score != null && (
        <span>
          <span className="text-green-500 font-semibold">Skills</span> {skills_score}/35
        </span>
      )}
    </div>
  );
}

/**
 * Card for an AggregatedJob inside the merged JobSearch feed.
 *
 * Deliberately NOT a modification of JobCard.jsx — JobCard is tightly
 * coupled to JobPosting's quick-apply/save flow and is reused elsewhere
 * (e.g. the standalone /jobcard route), so changing it to conditionally
 * handle a second job shape would risk that other usage. This is a
 * separate, simpler component instead: same visual language as JobCard
 * (logo, title, salary, View job / Apply button row) but no save/share
 * actions — those don't apply to a job PlanB doesn't own. Clicking the
 * card body goes to /aggregated-jobs/:id for full details; Apply applies
 * inline without leaving the list, mirroring AggregatedJobDetail's flow.
 */
function AggregatedJobCard({ job, matchScore, matchBreakdown, matchReasons, BACKEND_URL }) {
  const navigate = useNavigate();
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(Boolean(job.has_applied));
  const [applyError, setApplyError] = useState('');

  const logoSrc = resolveLogoSrc(job.company_logo, BACKEND_URL);
  const salary = formatSalary(job.salary_min, job.salary_max, job.salary_currency);
  const experience = formatExperience(job.experience_min, job.experience_max);

  const handleApply = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'job_seeker') {
      setApplyError('Only job seekers can apply to jobs.');
      return;
    }

    setApplying(true);
    setApplyError('');
    try {
      await api.post(`/aggregated-jobs/${job.id}/apply`);
      setApplied(true);
    } catch (err) {
      if (err.response?.status === 409) {
        setApplied(true);
      } else {
        setApplyError(err.response?.data?.message || 'Failed to apply.');
      }
    } finally {
      setApplying(false);
    }
  };

  return (
    <Link
      to={`/aggregated-jobs/${job.id}`}
      className="block pb-job-card !p-4"
    >
      {/* Top row: logo, title, salary */}
      <div className="flex items-start gap-3">
        {logoSrc ? (
          <img src={logoSrc} alt="" className="w-9 h-9 rounded-full object-cover shrink-0 border border-gray-100" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-blue-500 flex items-center justify-center font-bold text-white shrink-0">
            {job.company_name?.charAt(0)?.toUpperCase() || 'J'}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-heading font-bold text-pb-text leading-snug text-sm flex flex-wrap items-baseline gap-x-1.5">
              <span>{job.title}</span>
              <span className="font-normal text-xs text-gray-500">
                {job.company_name}
                {job.location && <span> · {job.location}</span>}
              </span>
            </h3>

            <div className="flex items-center gap-1.5 shrink-0">
              {matchScore != null && (
                <span
                  className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white whitespace-nowrap"
                  style={{ backgroundColor: matchScore >= 80 ? '#10B981' : matchScore >= 50 ? '#7047FF' : '#F59E0B' }}
                >
                  {matchScore}%
                </span>
              )}
              {salary && (
                <span className="text-base font-bold text-gray-900 whitespace-nowrap">
                  {salary}
                </span>
              )}
            </div>
          </div>

          <p className="text-xs text-gray-500 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
            {job.job_type && <span>{job.job_type.replace(/_/g, ' ')}</span>}
            {experience && <span>· {experience}</span>}
            {job.category && <span>· {job.category}</span>}
          </p>
        </div>
      </div>

      {/* Match reason badges — mirrors JobCard's badge row */}
      {matchReasons && (matchReasons.locationMatch || matchReasons.roleMatch || matchReasons.matchedSkills?.length > 0) && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {matchReasons.locationMatch && (
            <span className="flex items-center gap-0.5 text-[11px] font-medium px-2 py-0.5 rounded-full bg-purple-50 text-purple-600">
              ✓ {matchReasons.matchedLocation || 'Preferred location'}
            </span>
          )}
          {matchReasons.roleMatch && (
            <span className="flex items-center gap-0.5 text-[11px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">
              ✓ Role fits your experience
            </span>
          )}
          {matchReasons.matchedSkills?.slice(0, 2).map((s, i) => (
            <span key={i} className="flex items-center gap-0.5 text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-600">
              ✓ {s}
            </span>
          ))}
        </div>
      )}

      {/* Numeric match breakdown — location/career/skills sub-scores */}
      <MatchBreakdownRow breakdown={matchBreakdown} />

      {/* Bottom row: source tag / counts / View job / Apply button — matches JobCard sizing */}
      <div className="flex items-center justify-between mt-1 gap-2">
        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
          {job.source_name ? `via ${job.source_name}` : 'Partner source'}
        </span>

        {(job.applications_count != null || job.views_count != null) && (
          <div className="flex items-center gap-3 text-[11px] text-gray-400 shrink-0">
            {job.applications_count != null && (
              <span className="flex items-center gap-1">
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <AnimatedCount value={job.applications_count} /> applied
              </span>
            )}
            {job.views_count != null && (
              <span className="flex items-center gap-1">
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <AnimatedCount value={job.views_count} /> views
              </span>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs font-semibold text-gray-500 hover:text-gray-700 hover:underline">
            View job
          </span>
          <button
            onClick={handleApply}
            disabled={applying || applied}
            className={`text-xs font-bold px-4 py-2 rounded-full transition ${
              applied
                ? 'bg-green-50 text-green-600 cursor-default'
                : 'bg-pb-purple text-white hover:bg-[#5B35E0] disabled:opacity-50'
            }`}
          >
            {applied ? '✓ Applied' : applying ? 'Applying...' : 'Apply'}
          </button>
        </div>
      </div>

      {applyError && (
        <p className="text-[10px] text-red-500 mt-1 text-right">{applyError}</p>
      )}
    </Link>
  );
}

export default AggregatedJobCard;